/**
 * Display helpers for the Running Account Bill document form.
 * Comparison math here is Current - Previous only. It must never replace
 * computeCpwdRABillAmounts / raBillCalculationRouter results.
 */

export const SECTION_CODES = {
  WORK_VALUE: 'WORK_VALUE',
  RECOVERIES: 'RECOVERIES',
  TOTALS: 'TOTALS',
};

const safeList = (value) => (Array.isArray(value) ? value : []);

export const toNumericAmount = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/,/g, '').trim();
  if (!cleaned || cleaned === '-') return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const sincePreviousAmount = (currentValue, previousValue) => (
  toNumericAmount(currentValue) - toNumericAmount(previousValue)
);

export const formatIndianAmount = (value) => (
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(toNumericAmount(value))
);

export const parseIndianAmountInput = (raw) => {
  if (raw == null || raw === '') return '';
  const cleaned = String(raw).replace(/,/g, '').replace(/[^\d.-]/g, '').trim();
  if (!cleaned || cleaned === '-' || cleaned === '.') return cleaned;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : '';
};

export const additionalRowIdentity = (row) => {
  if (!row || typeof row !== 'object') return '';
  const id = String(row.row_id || '').trim();
  if (id) return `id:${id}`;
  const code = String(row.row_code || '').trim();
  if (code) return `code:${code}`;
  return '';
};

const slugFromDescription = (description) => {
  const slug = String(description || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);
  return slug || 'CUSTOM_ROW';
};

const createRowId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createAdditionalBillRow = ({
  line_label = '',
  description = '',
  section_code = SECTION_CODES.WORK_VALUE,
  current_value = 0,
  sequence = 90,
  row_id,
  row_code,
} = {}) => {
  const id = row_id || createRowId();
  const code = row_code || `${slugFromDescription(description)}_${String(id).replace(/-/g, '').slice(0, 8)}`;
  return {
    row_id: id,
    row_code: code,
    section_code: section_code || SECTION_CODES.WORK_VALUE,
    line_label: String(line_label || '').trim(),
    description: String(description || '').trim(),
    sequence: Number.isFinite(Number(sequence)) ? Number(sequence) : 90,
    current_value: toNumericAmount(current_value),
    active: true,
  };
};

export const normalizeAdditionalBillRows = (rows) => (
  safeList(rows)
    .filter((row) => row && typeof row === 'object' && row.active !== false)
    .map((row, index) => createAdditionalBillRow({
      ...row,
      sequence: row.sequence ?? (90 + index),
    }))
    .filter((row) => additionalRowIdentity(row))
);

export const getBillFieldAmount = (bill, fieldKey, altKeys = []) => {
  if (!bill || typeof bill !== 'object') return 0;
  const keys = [fieldKey, ...safeList(altKeys)].filter(Boolean);
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(bill, key) && bill[key] !== '' && bill[key] != null) {
      return toNumericAmount(bill[key]);
    }
  }
  return 0;
};

export const STANDARD_WORK_VALUE_ROWS = [
  {
    row_code: 'AGREEMENT_AMOUNT',
    fieldKey: 'agreement_amount',
    label: 'Agreement Amount (Rs.)',
    editable: false,
  },
  {
    row_code: 'GROSS_WORK_DONE',
    fieldKey: 'upto_date_gross_work_done',
    altKeys: ['gross_value_of_work_done'],
    label: '1. Gross Value of Work done (Rs.)',
    editable: true,
  },
  {
    row_code: 'SEC_ADVANCE_MATERIAL',
    fieldKey: 'sec_advance_material',
    label: '2 (a). Upto date Secured Advance on material (Rs.)',
    editable: true,
  },
  {
    row_code: 'MOBILISATION_ADVANCE',
    fieldKey: 'mobilisation_advance',
    label: '2 (b). Mobilisation Advance (Rs.)',
    editable: true,
  },
  {
    row_code: 'CLAUSE_10CC_10CA',
    fieldKey: 'clause_10cc_10ca_payment',
    label: '2 (Clause 10CC / 10CA payment) (Rs.)',
    editable: true,
  },
  {
    row_code: 'ADVANCE_PAYMENT_NOT_MEASURED',
    fieldKey: 'advance_payment_workdone_not_measured',
    label: '2 (c). Advance payment (work done, not measured) (Rs.)',
    editable: true,
  },
  {
    row_code: 'UPTO_DATE_GROSS_VALUE',
    fieldKey: 'upto_date_gross_value_of_work_done',
    altKeys: ['upto_date_gross_inc_sec_adv', 'upto_date_gross_work_done'],
    label: '3. Upto date Gross Value of Work done — sum of above (Rs.)',
    editable: false,
    calculated: true,
  },
  {
    row_code: 'SECURED_ADVANCE_ADDED',
    fieldKey: 'secured_advance_added_in_this_bill',
    label: 'Secured Advance added in this bill (Rs.)',
    editable: false,
    calculated: true,
  },
  {
    row_code: 'GROSS_VALUE_PREVIOUS_RA',
    fieldKey: 'gross_value_as_per_previous_ra_bill',
    altKeys: ['previous_ra_gross_inc_sec_adv', 'previous_ra_upto_date_gross'],
    label: '4. Gross value as per Previous RA Bill (Rs.)',
    editable: false,
    calculated: true,
  },
  {
    row_code: 'NET_VALUE_SINCE_PREV',
    fieldKey: 'net_value_work_done_since_prev_bill',
    altKeys: ['current_bill_value'],
    label: '5 (a). Net value since Previous Bill (Rs.)',
    editable: false,
    calculated: true,
  },
  {
    row_code: 'NET_VALUE_EXCL_SEC_ADV',
    fieldKey: 'net_value_work_done_without_sec_adv',
    altKeys: ['current_bill_value'],
    label: '5 (b). Net Value excluding Secured Advance (Rs.)',
    editable: false,
    calculated: true,
  },
];

export const STANDARD_RECOVERY_ROWS = [
  {
    row_code: 'SECURITY_DEPOSIT',
    fieldKey: 'security_deposit',
    label: 'a) Security Deposit @ 2.5% (Rs.)',
    editable: false,
    calculated: true,
  },
  {
    row_code: 'GST_DEDUCTION',
    fieldKey: 'gst_deduction',
    label: 'b) GST @ 2% (Rs.)',
    editable: false,
    calculated: true,
  },
  {
    row_code: 'INCOME_TAX',
    fieldKey: 'income_tax_deduction',
    label: 'c) Income Tax @ 2% (Rs.)',
    editable: false,
    calculated: true,
  },
  {
    row_code: 'LABOUR_CESS',
    fieldKey: 'labour_cess',
    label: 'd) Labour Cess @ 1% (Rs.)',
    editable: false,
    calculated: true,
  },
  {
    row_code: 'ADDITIONAL_SD',
    fieldKey: 'additional_sd_special_items',
    label: 'e) Addl.SD for Spl.items (Rs.)',
    editable: true,
  },
  {
    row_code: 'TOTAL_RECOVERIES',
    fieldKey: 'total_recoveries',
    label: 'Total Recoveries',
    editable: false,
    calculated: true,
    isTotal: true,
  },
];

export const STANDARD_TOTAL_ROWS = [
  {
    row_code: 'TOTAL_DEDUCTIONS',
    fieldKey: 'total_deductions',
    altKeys: ['deductions_total'],
    label: '9. Total Deductions (7)+(8)',
    editable: false,
    calculated: true,
    isTotal: true,
  },
  {
    row_code: 'NET_PAYMENT_RECEIVED',
    fieldKey: 'net_payment_received',
    altKeys: ['net_payable'],
    label: '10. Net Payment Received (5)-(9) (Rs.)',
    editable: false,
    calculated: true,
    isTotal: true,
  },
];

export const buildStandardComparisonRows = (defs, currentBill, previousBill = null) => (
  safeList(defs).map((def) => {
    const currentValue = getBillFieldAmount(currentBill, def.fieldKey, def.altKeys);
    const previousValue = previousBill
      ? getBillFieldAmount(previousBill, def.fieldKey, def.altKeys)
      : 0;
    return {
      ...def,
      current_value: currentValue,
      previous_value: previousValue,
      since_previous: sincePreviousAmount(currentValue, previousValue),
    };
  })
);

const sortAdditionalRows = (rows) => (
  [...rows].sort((a, b) => {
    const seqDiff = Number(a.sequence || 0) - Number(b.sequence || 0);
    if (seqDiff !== 0) return seqDiff;
    return String(a.line_label || a.description || '').localeCompare(String(b.line_label || b.description || ''));
  })
);

export const unionAdditionalBillRows = (previousRows, currentRows) => {
  const previous = normalizeAdditionalBillRows(previousRows);
  const current = normalizeAdditionalBillRows(currentRows);
  const byIdentity = new Map();

  previous.forEach((row) => {
    const key = additionalRowIdentity(row);
    if (!key) return;
    byIdentity.set(key, {
      row_id: row.row_id,
      row_code: row.row_code,
      section_code: row.section_code,
      line_label: row.line_label,
      description: row.description,
      sequence: row.sequence,
      previous_value: toNumericAmount(row.current_value),
      current_value: 0,
      active: true,
    });
  });

  current.forEach((row) => {
    const key = additionalRowIdentity(row);
    if (!key) return;
    const existing = byIdentity.get(key);
    if (existing) {
      byIdentity.set(key, {
        ...existing,
        line_label: row.line_label || existing.line_label,
        description: row.description || existing.description,
        section_code: row.section_code || existing.section_code,
        sequence: row.sequence ?? existing.sequence,
        current_value: toNumericAmount(row.current_value),
      });
    } else {
      byIdentity.set(key, {
        row_id: row.row_id,
        row_code: row.row_code,
        section_code: row.section_code,
        line_label: row.line_label,
        description: row.description,
        sequence: row.sequence,
        previous_value: 0,
        current_value: toNumericAmount(row.current_value),
        active: true,
      });
    }
  });

  return sortAdditionalRows(Array.from(byIdentity.values())).map((row) => ({
    ...row,
    since_previous: sincePreviousAmount(row.current_value, row.previous_value),
    label: [row.line_label, row.description].filter(Boolean).join('. ').replace(/^\.\s*/, ''),
    editable: true,
  }));
};

export const carryForwardAdditionalRows = (previousRows, currentRows) => {
  const unioned = unionAdditionalBillRows(previousRows, currentRows);
  return unioned.map((row) => ({
    row_id: row.row_id,
    row_code: row.row_code,
    section_code: row.section_code,
    line_label: row.line_label,
    description: row.description,
    sequence: row.sequence,
    current_value: toNumericAmount(row.current_value),
    active: true,
  }));
};

export const additionalRowsForSection = (unionedRows, sectionCode) => (
  safeList(unionedRows).filter((row) => (row.section_code || SECTION_CODES.WORK_VALUE) === sectionCode)
);

export const pruneEmptyAdditionalBillRows = (rows) => (
  normalizeAdditionalBillRows(rows).filter((row) => (
    String(row.description || '').trim() || toNumericAmount(row.current_value) !== 0
  ))
);

export const recoveryRowLabelsWithRates = (defs, rates = null) => {
  if (!rates) return defs;
  const sd = rates.security_deposit_pct;
  const gst = rates.gst_pct;
  const it = rates.income_tax_pct;
  const cess = rates.labour_cess_pct;
  return defs.map((def) => {
    if (def.row_code === 'SECURITY_DEPOSIT' && sd != null) {
      return { ...def, label: `a) Security Deposit @ ${sd}% (Rs.)` };
    }
    if (def.row_code === 'GST_DEDUCTION' && gst != null) {
      return { ...def, label: `b) GST @ ${gst}% (Rs.)` };
    }
    if (def.row_code === 'INCOME_TAX' && it != null) {
      return { ...def, label: `c) Income Tax @ ${it}% (Rs.)` };
    }
    if (def.row_code === 'LABOUR_CESS' && cess != null) {
      return { ...def, label: `d) Labour Cess @ ${cess}% (Rs.)` };
    }
    return def;
  });
};
